// libs
import { useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { processText } from '@aytm/app/stats/page/components/lib/utils'
import { decodeText, encodeText } from '@aytm/app/stats/lib/util/helpers'

// components
import { InputTextarea } from 'primereact/inputtextarea'

// styles
import style from '@aytm/app/stats/page/question-views/components/styles/QuestionTitle.module.scss'

const MAX_TITLE_LENGTH = 240

// how many frames the field keeps asking for focus while its element is still out of the document
const FOCUS_ATTEMPTS = 10

// the cap counts UTF-16 units, so a cut can land between the two halves of an emoji
const clipTitle = value => {
  const clipped = value.slice(0, MAX_TITLE_LENGTH)
  return /[\uD800-\uDBFF]$/.test(clipped) ? clipped.slice(0, -1) : clipped
}

const onKeyDown = event => 13 == event.keyCode && event.preventDefault()

const QuestionTitle = ({ question, title, editing, disabled, setTitle }) => {
  const callbackRef = useRef(null)
  const fieldRef = useRef(null)

  const questionTitle = useMemo(
    () =>
      ['dlv', 'dlc', 'dlx', 'dld', 'dlt'].includes(question.type)
        ? question.text.length
          ? `${question.text}${question.textAppendix ? ' ' + question.textAppendix : ''}`
          : 'New custom chart'
        : processText(('string' == typeof question.header && question.header) || question.text),
    [question.header, question.text, question.textAppendix, question.type]
  )

  const onChange = useCallback(
    event => {
      const text = encodeText(event.target.value)
      if (title == text) return

      setTitle(text)
    },
    [title, setTitle]
  )

  const onPaste = useCallback(
    event => {
      event.preventDefault()

      let text =
        (event.clipboardData || window.clipboardData).getData('Text') || event.clipboardData.getData('text/plain')
      text = text.replace(/(\r\n|\n|\r|\t|\v)/gm, ' ').trim()

      const el = event.currentTarget

      const l = text.length
      const start = el.selectionStart
      const end = el.selectionEnd

      const valueBefore = el.value.slice(0, start)
      const valueAfter = el.value.slice(end)
      text = encodeText(clipTitle(`${valueBefore}${text}${valueAfter}`))

      if (title != text) setTitle(text)

      callbackRef.current = () => {
        el.setSelectionRange(start + l, start + l)
        el.blur()
        el.focus()
      }
    },
    [title, setTitle]
  )

  useEffect(() => {
    if (null !== callbackRef.current && 'function' == typeof callbackRef.current) {
      callbackRef.current()
      callbackRef.current = null
    }
  }, [title])

  // A question view can be drawn before its element is put in the document, and focusing a node that
  // is not in the document does nothing at all -- no error, and no second attempt. So the field asks
  // again on the following frames until it is attached, which is the tick the insertion happens on.
  useEffect(() => {
    if (!editing || disabled) return

    let frame = 0
    let attemptsLeft = FOCUS_ATTEMPTS
    const focusWhenAttached = () => {
      frame = 0
      const el = fieldRef.current
      if (!el) return
      if (el.isConnected) {
        el.focus()
        return
      }
      if (0 < attemptsLeft--) frame = requestAnimationFrame(focusWhenAttached)
    }
    focusWhenAttached()

    return () => {
      if (frame) cancelAnimationFrame(frame)
    }
  }, [editing, disabled])

  return editing && !disabled ? (
    <div className="p-fluid">
      <InputTextarea
        id={`title-${question.id}`}
        ref={fieldRef}
        className={classNames('p-inputtext-rounded', { 'p-invalid': !title.trim().length })}
        placeholder="Full title for reporting purposes"
        value={decodeText(title)}
        rows={1}
        maxLength={MAX_TITLE_LENGTH}
        autoResize
        onChange={onChange}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
      />
    </div>
  ) : (
    <div data-testid="stat-question-title" className={style.text} dangerouslySetInnerHTML={{ __html: questionTitle }} />
  )
}

QuestionTitle.propTypes = {
  question: PropTypes.object,
  title: PropTypes.string,
  editing: PropTypes.bool,
  disabled: PropTypes.bool,
  setTitle: PropTypes.func
}

QuestionTitle.displayName = 'QuestionTitle'

export default QuestionTitle
